export interface TouchState {
  dx: number;
  dz: number;
  turn: number;
  orbitV: number;
  firing: boolean;
}

interface Stick {
  active: boolean;
  id: number;
  originX: number;
  originY: number;
  dx: number;
  dy: number;
}

const RADIUS = 55; // max knob travel in px
const DEAD   = 0.12;

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v;
}

export class TouchControls {
  readonly enabled: boolean;

  private left:  Stick = { active: false, id: -1, originX: 0, originY: 0, dx: 0, dy: 0 };
  private right: Stick = { active: false, id: -1, originX: 0, originY: 0, dx: 0, dy: 0 };

  private fireActive = false;
  private fireId     = -1;

  private tapFiring  = false;
  private tapId      = -1;

  private leftZone!:  HTMLElement;
  private leftBase!:  HTMLElement;
  private leftKnob!:  HTMLElement;

  private rightZone!: HTMLElement;
  private rightBase!: HTMLElement;
  private rightKnob!: HTMLElement;

  private fireBtn!:   HTMLElement;
  private wrapper!:   HTMLElement;

  constructor() {
    this.enabled = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    if (this.enabled) this.init();
  }

  getState(): TouchState {
    if (!this.enabled) return { dx: 0, dz: 0, turn: 0, orbitV: 0, firing: false };

    let dx = this.left.active ? this.left.dx : 0;
    let dz = this.left.active ? this.left.dy : 0;
    const moveLen = Math.sqrt(dx * dx + dz * dz);
    if (moveLen > 1) { dx /= moveLen; dz /= moveLen; }
    if (Math.abs(dx) < DEAD) dx = 0;
    if (Math.abs(dz) < DEAD) dz = 0;

    let turn   = this.right.active ? clamp(this.right.dx,  -1, 1) : 0;
    let orbitV = this.right.active ? clamp(-this.right.dy, -1, 1) : 0;
    if (Math.abs(turn)   < DEAD) turn   = 0;
    if (Math.abs(orbitV) < DEAD) orbitV = 0;

    const firing = this.fireActive || this.tapFiring;
    // Tap-to-fire is a single shot per tap — consume after one read
    if (this.tapFiring) this.tapFiring = false;
    return { dx, dz, turn, orbitV, firing };
  }

  /** Reset all active touch state (call when a modal opens, etc.) */
  reset() {
    if (!this.enabled) return;
    this.resetStick(this.left,  this.leftBase,  this.leftKnob);
    this.resetStick(this.right, this.rightBase, this.rightKnob);
    this.fireActive = false;
    this.fireId     = -1;
    this.tapFiring  = false;
    this.tapId      = -1;
    this.fireBtn?.classList.remove('pressed');
  }

  destroy() {
    this.wrapper?.remove();
  }

  // ── Build ──────────────────────────────────────────────────

  private init() {
    this.injectCSS();
    this.buildDOM();
    this.attachListeners();
    this.watchMenuOpen();
  }

  /** Reset all touch state whenever the upgrade menu opens so thumbs don't get stuck. */
  private watchMenuOpen() {
    const menu = document.getElementById('upgrade-menu');
    if (!menu) return;
    new MutationObserver(() => {
      if (menu.classList.contains('open')) this.reset();
    }).observe(menu, { attributes: true, attributeFilter: ['class'] });
  }

  private injectCSS() {
    const style = document.createElement('style');
    style.textContent = `
      /* ── Touch control zones — each covers half the screen ── */
      .tc-zone {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 50%;
        pointer-events: all;
        touch-action: none;
        -webkit-tap-highlight-color: transparent;
      }
      .tc-zone-left  { left: 0; }
      .tc-zone-right { right: 0; }

      /* Dynamic base circle (appears at touch origin) */
      .tc-base {
        position: absolute;
        width: 110px;
        height: 110px;
        border-radius: 50%;
        border: 2px solid rgba(120,180,255,0.35);
        background: rgba(80,130,255,0.07);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.08s;
        pointer-events: none;
        transform: translate(-50%, -50%);
      }
      .tc-base.visible { opacity: 1; }

      /* Knob */
      .tc-knob {
        width: 54px;
        height: 54px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 35%,
          rgba(180,210,255,0.4), rgba(80,130,255,0.14));
        border: 2px solid rgba(120,180,255,0.5);
        box-shadow: 0 4px 14px rgba(0,0,0,0.4);
        pointer-events: none;
        will-change: transform;
      }

      /* Fire button */
      .tc-fire {
        position: absolute;
        width: 82px;
        height: 82px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 35%, #ff6b6b, #c0392b);
        border: 3px solid rgba(255,120,120,0.5);
        box-shadow: 0 0 18px rgba(255,80,80,0.4),
                    inset 0 2px 5px rgba(255,255,255,0.16);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 30px;
        pointer-events: all;
        touch-action: none;
        -webkit-tap-highlight-color: transparent;
        cursor: pointer;
        transition: transform 0.05s, box-shadow 0.05s;
        user-select: none;
      }
      .tc-fire.pressed {
        transform: scale(0.90);
        box-shadow: 0 0 28px rgba(255,80,80,0.7),
                    inset 0 2px 5px rgba(255,255,255,0.16);
      }

      /* ── Mobile layout overrides ── */
      @media (hover: none) and (pointer: coarse) {
        /* Hide the keyboard shortcut hints */
        #tab-hint { display: none !important; }
        /* Hide fire button — tap-to-shoot replaces it */
        .tc-fire { display: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  private buildDOM() {
    this.wrapper = document.createElement('div');
    this.wrapper.id = 'touch-controls';
    this.wrapper.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:11;user-select:none;-webkit-user-select:none;';

    // ── Left zone (movement) — full left half of screen ──
    this.leftZone = document.createElement('div');
    this.leftZone.className = 'tc-zone tc-zone-left';
    this.leftBase = document.createElement('div');
    this.leftBase.className = 'tc-base';
    this.leftKnob = document.createElement('div');
    this.leftKnob.className = 'tc-knob';
    this.leftBase.appendChild(this.leftKnob);
    this.leftZone.appendChild(this.leftBase);

    // ── Right zone (turn + camera tilt) — full right half of screen ──
    this.rightZone = document.createElement('div');
    this.rightZone.className = 'tc-zone tc-zone-right';
    this.rightBase = document.createElement('div');
    this.rightBase.className = 'tc-base';
    this.rightKnob = document.createElement('div');
    this.rightKnob.className = 'tc-knob';
    this.rightBase.appendChild(this.rightKnob);
    this.rightZone.appendChild(this.rightBase);

    // ── Fire button ──
    this.fireBtn = document.createElement('div');
    this.fireBtn.className = 'tc-fire';
    Object.assign(this.fireBtn.style, { bottom: '80px', right: '30px' });
    this.fireBtn.textContent = '🔥';

    this.wrapper.appendChild(this.leftZone);
    this.wrapper.appendChild(this.rightZone);
    this.wrapper.appendChild(this.fireBtn);
    document.body.appendChild(this.wrapper);
  }

  // ── Event listeners ────────────────────────────────────────

  private attachListeners() {
    this.bindZone(this.leftZone,  this.left,  this.leftBase,  this.leftKnob);
    this.bindZone(this.rightZone, this.right, this.rightBase, this.rightKnob);

    // Tap-to-fire — any touch not on an interactive element fires.
    // Uses document-level listener so it works on the canvas, HUD, or any dead area.
    const interactiveSelector = 'button, input, select, a, .tc-zone-left, .tc-fire, #upgrade-menu.open, #stats-popup.open, #auto-fire-btn, #mute-btn, #help-btn, #exit-btn, #mobile-lb-btn, #leaderboard';
    document.addEventListener('touchstart', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(interactiveSelector)) return;
      if (this.tapId === -1) {
        this.tapId = e.changedTouches[0].identifier;
        this.tapFiring = true;
      }
    }, { passive: true });

    const tapRelease = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.tapId) {
          this.tapId = -1;
          this.tapFiring = false;
          break;
        }
      }
    };
    document.addEventListener('touchend', tapRelease, { passive: true });
    document.addEventListener('touchcancel', () => {
      this.tapId = -1;
      this.tapFiring = false;
    }, { passive: true });

    // Fire button
    this.fireBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.fireId === -1) {
        this.fireId = e.changedTouches[0].identifier;
        this.fireActive = true;
        this.fireBtn.classList.add('pressed');
      }
    }, { passive: false });

    const fireRelease = (e: TouchEvent) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.fireId) {
          this.fireId     = -1;
          this.fireActive = false;
          this.fireBtn.classList.remove('pressed');
          break;
        }
      }
    };
    this.fireBtn.addEventListener('touchend',    fireRelease, { passive: false });
    this.fireBtn.addEventListener('touchcancel', (_e) => {
      this.fireId     = -1;
      this.fireActive = false;
      this.fireBtn.classList.remove('pressed');
    }, { passive: false });
  }

  private bindZone(zone: HTMLElement, stick: Stick, base: HTMLElement, knob: HTMLElement) {
    zone.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (stick.active) return;
      const t = e.changedTouches[0];
      stick.active  = true;
      stick.id      = t.identifier;
      stick.originX = t.clientX;
      stick.originY = t.clientY;
      stick.dx = 0;
      stick.dy = 0;

      // Position the base circle at the touch point
      const rect = zone.getBoundingClientRect();
      base.style.left = `${t.clientX - rect.left}px`;
      base.style.top  = `${t.clientY - rect.top}px`;
      base.classList.add('visible');
      knob.style.transform = 'translate(0px, 0px)';
    }, { passive: false });

    zone.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!stick.active) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== stick.id) continue;
        const rawX = t.clientX - stick.originX;
        const rawY = t.clientY - stick.originY;
        const dist  = Math.sqrt(rawX * rawX + rawY * rawY);
        const clamped = Math.min(dist, RADIUS);

        stick.dx = dist > 0 ? (rawX / dist) * (clamped / RADIUS) : 0;
        stick.dy = dist > 0 ? (rawY / dist) * (clamped / RADIUS) : 0;

        const vx = dist > 0 ? (rawX / dist) * clamped : 0;
        const vy = dist > 0 ? (rawY / dist) * clamped : 0;
        knob.style.transform = `translate(${vx}px, ${vy}px)`;
        break;
      }
    }, { passive: false });

    const release = (e: TouchEvent) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== stick.id) continue;
        this.resetStick(stick, base, knob);
        break;
      }
    };
    const cancel = () => this.resetStick(stick, base, knob);

    zone.addEventListener('touchend',    release, { passive: false });
    zone.addEventListener('touchcancel', cancel,  { passive: false });
  }

  private resetStick(stick: Stick, base: HTMLElement, knob: HTMLElement) {
    stick.active = false;
    stick.id     = -1;
    stick.dx     = 0;
    stick.dy     = 0;
    base.classList.remove('visible');
    knob.style.transform = 'translate(0px, 0px)';
  }
}
